import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addControlledSetNumberAPI,
  fetchControlledSetNumbersAPI,
  removeControlledSetNumberAPI,
} from "./controlledSetNumberAPI";
import { showModal } from "../statusModal/statusModal";

const initialState = {
  controlledSetNumbers: {},
  fetchStatus: "idle",
  addStatus: "idle",
  removeStatus: "idle",
  error: null,
};

const matchesTournament = (entry, tournamentId) =>
  String(entry?.tournamentId) === String(tournamentId);

const extractTournamentSetNumbers = (entries, tournamentId) =>
  (Array.isArray(entries) ? entries : [])
    .filter((entry) => matchesTournament(entry, tournamentId))
    .map((entry) => entry.setNumber);

export const fetchControlledSetNumbers = createAsyncThunk(
  "controlledSetNumbers/fetchControlledSetNumbers",
  async (tournamentId, { dispatch, rejectWithValue }) => {
    try {
      const data = await fetchControlledSetNumbersAPI(tournamentId);
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء جلب بيانات المنفذين",
          status: "error",
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete admins"
      );
    }
  }
);

export const addControlledSetNumber = createAsyncThunk(
  "controlledSetNumbers/addControlledSetNumber",
  async (
    { adminId, tournamentId, setNumber },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await addControlledSetNumberAPI(
        adminId,
        tournamentId,
        setNumber
      );
      dispatch(
        showModal({
          message: "تم اضافة الجداول الى الموظف",
          status: "success",
        })
      );
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء اضافة الجداول الى الموظف",
          status: "error",
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admins"
      );
    }
  }
);

export const removeControlledSetNumber = createAsyncThunk(
  "controlledSetNumbers/removeControlledSetNumber",
  async (
    { adminId, tournamentId, setNumber },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await removeControlledSetNumberAPI(
        adminId,
        tournamentId,
        setNumber
      );
      dispatch(
        showModal({
          message: "تم ازالة الجداول من الموظف",
          status: "success",
        })
      );
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء ازالة الجداول من الموظف",
          status: "error",
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admins"
      );
    }
  }
);

const controlledSetNumbersSlice = createSlice({
  name: "controlledSetNumbers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchControlledSetNumbers.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchControlledSetNumbers.fulfilled, (state, action) => {
        const { tournamentId, data } = action.payload;
        state.fetchStatus = "succeeded";
        state.controlledSetNumbers[tournamentId] = data.reduce((acc, entry) => {
          acc[entry.adminId] = entry.setNumbers;
          return acc;
        }, {});
      })
      .addCase(fetchControlledSetNumbers.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload;
      })
      .addCase(addControlledSetNumber.pending, (state) => {
        state.addStatus = "loading";
        state.error = null;
      })
      .addCase(addControlledSetNumber.fulfilled, (state, action) => {
        const { adminId, tournamentId, data } = action.payload;
        if (!data?.controlledSetNumbers) {
          console.error("Controlled set numbers are missing in the response.");
          return;
        }

        if (!state.controlledSetNumbers[tournamentId]) {
          state.controlledSetNumbers[tournamentId] = {};
        }

        if (!state.controlledSetNumbers[tournamentId][adminId]) {
          state.controlledSetNumbers[tournamentId][adminId] = [];
        }

        const newSetNumbers = extractTournamentSetNumbers(
          data.controlledSetNumbers,
          tournamentId
        );

        state.controlledSetNumbers[tournamentId][adminId] = [
          ...new Set([
            ...state.controlledSetNumbers[tournamentId][adminId],
            ...newSetNumbers,
          ]),
        ];

        state.addStatus = "succeeded";
      })
      .addCase(addControlledSetNumber.rejected, (state, action) => {
        state.addStatus = "failed";
        state.error = action.payload;
      })
      .addCase(removeControlledSetNumber.pending, (state) => {
        state.removeStatus = "loading";
        state.error = null;
      })
      .addCase(removeControlledSetNumber.fulfilled, (state, action) => {
        const { adminId, tournamentId, data } = action.payload;
        const remainingSetNumbers = extractTournamentSetNumbers(
          data?.controlledSetNumbers,
          tournamentId
        );

        if (state.controlledSetNumbers[tournamentId]?.[adminId]) {
          if (remainingSetNumbers.length === 0) {
            delete state.controlledSetNumbers[tournamentId][adminId];
          } else {
            state.controlledSetNumbers[tournamentId][adminId] =
              remainingSetNumbers;
          }

          if (
            Object.keys(state.controlledSetNumbers[tournamentId]).length === 0
          ) {
            delete state.controlledSetNumbers[tournamentId];
          }
        }

        state.removeStatus = "succeeded";
      })
      .addCase(removeControlledSetNumber.rejected, (state, action) => {
        state.removeStatus = "failed";
        state.error = action.payload;
      });
  },
});

export default controlledSetNumbersSlice.reducer;
